"use client";

import {
	autoPlacement,
	offset,
	safePolygon,
	shift,
	useFloating,
	useFocus,
	useHover,
	useInteractions,
} from "@floating-ui/react";
import type { ProductCard as ProductCardType } from "@raumagarn/formatters";
import { clsx } from "@trydig/ui";
import { type PropsWithChildren, useState } from "react";
import { Plus } from "~/components/icons/32/Plus";
import { ProductCard } from "~/features/products/ProductCard/ProductCard";
import styles from "./TaggedImageBlok.module.css";

export const ClientTag = ({
	product,
}: PropsWithChildren<{
	product: Omit<ProductCardType, "relatedProducts" | "variants">;
}>) => {
	const [open, setOpen] = useState(false);

	const { refs, floatingStyles, context } = useFloating({
		open,
		onOpenChange: setOpen,
		strategy: "absolute",
		middleware: [
			offset(5),
			shift(),
			autoPlacement((state) => {
				return {
					rootBoundary: "document",
					elementContext: "reference",
					padding: {
						top: state.rects.floating.height / 2,
						bottom: state.rects.floating.height / 2,
					},

					allowedPlacements: ["bottom", "top"],
				};
			}),
		],
	});

	const focus = useFocus(context);

	const hover = useHover(context, {
		handleClose: safePolygon(),
	});

	const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus]);

	return (
		<>
			<button
				ref={refs.setReference}
				data-product={product?.id}
				className={clsx(styles.tag__client, open && styles.active)}
				{...getReferenceProps()}
			>
				<Plus />
			</button>
			{open && (
				<div
					className={styles.tagCardContent}
					ref={refs.setFloating}
					style={floatingStyles}
					{...getFloatingProps({ tabIndex: 1 })}
				>
					<ProductCard productCard={product} />
				</div>
			)}
		</>
	);
};
